import { RedoOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { CiLocationOn } from 'react-icons/ci';
import { MapContainer, Marker } from 'react-leaflet';

import icon from '../../components/map/constants';
/**
 * Хүргэлт үүсгэх дэлгэц дээрх хаягийн зурагтай (components/map/AddressMap.js)
 * ижил бүрэлдэхүүн, ижил `pp_map_panel` харагдацыг хуваалцана.
 */
import { BaseLayers, MapCenterView, MapClickHandler } from '../../components/map/mapParts';
import '../../components/map/style.less';
import { fetchAddressUnit } from '../../utils/addressHelper';

const { Text } = Typography;

/** Улаанбаатарын төв — байршил сонгоогүй үед газрын зургийн эхлэх цэг */
const DEFAULT_CENTER = { lat: 47.9123565, lon: 106.9151432 };

const DEFAULT_REGION_ID = '11';

/** Газрын зураг болон хайлтын хэсгийн өндөр */
const PANEL_HEIGHT = 320;

/** Блок нь формын бусад талбартай нэг ирмэгт эгнэнэ */
const MAX_WIDTH = 960;

/** Координатыг уншигдахуйц болгоно (API-аас мөр байдлаар ирж болно) */
const formatPoint = ({ lat, lon }) => `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;

/**
 * Салбарын байршил сонгох газрын зураг + хаягийн хайлт.
 *
 * Салбар НЭМЭХ ба ЗАСАХ хоёр дэлгэц энэ компонентыг хуваалцана — өмнө нь зөвхөн
 * засах формд байсан тул шинээр үүсгэсэн салбар координатгүй (эсвэл бүгд ижил
 * хатуу бичсэн координаттай) хадгалагддаг байв.
 *
 * Сонгосон цэгийг формын `map_position` талбарт `[lat, lon]` хэлбэрээр бичнэ —
 * `form.validateFields()` түүнийг заавал гэж шалгана.
 *
 * @param {object} props.form эцэг формын instance
 * @param {Function} [props.setPosition] сонгосон цэгийг эцэгт мэдэгдэх (сонголттой)
 * @param {*} [props.resetKey] өөрчлөгдөхөд хадгалагдсан байршлыг дахин уншина
 */
const BranchLocationPicker = ({ form, setPosition, resetKey }) => {
  const [addressMap, setAddressMap] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [aimagList, setAimagList] = useState([]);
  const [aimagLoading, setAimagLoading] = useState(false);
  const [regionId, setRegionId] = useState(DEFAULT_REGION_ID);
  const [searchKey, setSearchKey] = useState('');
  const [aimagValue, setAimagValue] = useState(null);
  const [searching, setSearching] = useState(false);
  // Сонгосон цэг биш, зөвхөн зургийн харагдац — дарсан цэг рүү зөөнө
  const [center, setCenter] = useState(DEFAULT_CENTER);
  // Leaflet-д контейнер хэмжээтэй болсны дараа mount хийнэ (эс бөгөөс сааралдана)
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  /** Сонгосон цэг — маркер болон формын `map_position`-ыг тодорхойлно */
  const selectedPoint = selectedAddress?.location;

  // Сонгосон цэгийг формд болон эцэг компонентод дамжуулна
  useEffect(() => {
    if (!selectedPoint) return;

    const nextPosition = [selectedPoint.lat, selectedPoint.lon];
    setPosition?.(nextPosition);
    form.setFieldsValue({ map_position: nextPosition });
    //eslint-disable-next-line
  }, [selectedPoint?.lat, selectedPoint?.lon]);

  // Хадгалагдсан байршлыг газрын зураг дээр буулгана (засах горим)
  useEffect(() => {
    const location = form.getFieldValue('location');
    const point = location?.coordinates?.length
      ? { lat: location.coordinates[0], lon: location.coordinates[1] }
      : null;

    if (point) {
      setSelectedAddress({ location: point });
      setCenter(point);
    }
    // Эхлэхэд орчмын хаягуудыг харуулна — жагсаалт хоосон нээгдэхгүй
    getNearbyAddresses(point ?? DEFAULT_CENTER);
    //eslint-disable-next-line
  }, [resetKey]);

  useEffect(() => {
    setAimagLoading(true);
    fetchAddressUnit()
      .then(({ docs }) => {
        const aimags = docs.map((d) => ({
          label: d?.name ?? d?._source?.name_full_mn ?? d?._source?.name_mn,
          value: d.name,
          id: d._id,
        }));
        setAimagList(aimags);

        const defaultAimag = aimags.find((a) => a.id === DEFAULT_REGION_ID);
        if (defaultAimag) {
          setAimagValue(defaultAimag.value);
          setRegionId(defaultAimag.id);
        }
      })
      .finally(() => setAimagLoading(false));
  }, []);

  const getNearbyAddresses = (params) => {
    setSearching(true);
    axios
      .get(`${process.env.REACT_APP_ADDRESS_URL_V2}/search-nearby-address`, {
        params,
        headers: { 'X-Caller-Type': 'internal' },
      })
      .then(({ data = {} }) => {
        setAddressMap(data?.result?.length ? data.result : []);
      })
      .catch((err) => {
        console.error('getNearbyAddresses: ', err);
      })
      .finally(() => setSearching(false));
  };

  /**
   * Газрын зураг дээр дарахад тухайн цэгийг салбарын байршил болгож,
   * ойролцоох хаягуудыг татна. Салбарын байршил нь бүртгэлтэй хаягтай
   * таарахгүй байж болох тул шууд цэг сонгох боломжийг хэвээр үлдээв.
   */
  const onMapClick = ({ lat, lng }) => {
    const point = { lat, lon: lng };
    setSelectedAddress({ location: point });
    setCenter(point);
    getNearbyAddresses(point);
  };

  /** Жагсаалтаас хаяг сонгох — id-аар хайж бүтэн объектыг өгнө */
  const onSelectAddress = (addressId) => {
    const address = addressMap.find((item) => item.addressId === addressId);
    if (!address) return;

    setSelectedAddress(address);
    if (address?.location) setCenter(address.location);
  };

  const resetView = () => setCenter(selectedPoint ?? DEFAULT_CENTER);

  const onSearch = () => {
    if (!aimagValue) {
      message.error('Хот/Аймаг сонгоно уу.');
      return;
    }
    if (!searchKey) {
      message.error('Хаяг оруулна уу.');
      return;
    }
    getNearbyAddresses({ searchAddressText: searchKey, regionId });
  };

  return (
    <>
      {/* Сонгосон цэгийн координат — газрын зураг дээр дарахад бөглөгдөнө */}
      <Form.Item
        name="map_position"
        hidden
        rules={[
          {
            required: true,
            validator: (_, value) => {
              if (!value) {
                message.error('Газрын зураг дээрээс сонгоно уу.');
                return Promise.reject(new Error('Газрын зураг дээрээс сонгоно уу.'));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input />
      </Form.Item>

      <div className="pp_map_panel" style={{ maxWidth: MAX_WIDTH }}>
        <Text style={{ display: 'block' }}>
          <Text type="danger">*</Text> Газрын зураг дээрээс салбарын байршлаа сонгоно уу
        </Text>
        {/**
         * Сонгогдсон эсэхийг тодорхой харуулна. Хадгалагдсан байршил нь
         * бүртгэлтэй хаягтай холбоогүй байж болох тул жагсаалтад тодрохгүй —
         * тэр үед координатаар нь баталгаажуулна.
         */}
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          {selectedPoint
            ? `Сонгосон байршил: ${selectedAddress?.name ?? formatPoint(selectedPoint)}`
            : 'Байршил сонгоогүй байна.'}
        </Text>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <div className="pp_map_panel__canvas" style={{ height: PANEL_HEIGHT }}>
              {ready && (
                <MapContainer
                  center={[center.lat, center.lon]}
                  zoom={16}
                  scrollWheelZoom={false}
                  doubleClickZoom={false}
                  style={{ height: '100%', width: '100%' }}
                >
                  <BaseLayers />
                  <MapClickHandler onClick={onMapClick} />
                  {selectedPoint && (
                    <Marker position={[selectedPoint.lat, selectedPoint.lon]} icon={icon} />
                  )}
                  <MapCenterView center={center} />
                </MapContainer>
              )}

              <Tooltip title="Сонгосон байршил руу буцах" placement="left">
                <Button
                  className="pp_map_panel__reset"
                  icon={<RedoOutlined rotate={270} />}
                  onClick={resetView}
                />
              </Tooltip>
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {/* Хайлтын хүрээ — хаягийн талбар биш тул Form.Item-гүй */}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Хайх хот/аймаг
                </Text>
                <Select
                  placeholder="Сонгох"
                  options={aimagList}
                  loading={aimagLoading}
                  value={aimagValue || undefined}
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  onChange={(value, option) => {
                    setAimagValue(value);
                    setRegionId(option.id);
                  }}
                />
              </div>

              <Input.Search
                placeholder="Байр, байгууламжийн нэрээр хайх"
                enterButton="Хайх"
                allowClear
                loading={searching}
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                onSearch={onSearch}
              />

              <div className="pp_map_panel__results" style={{ height: PANEL_HEIGHT - 104 }}>
                {/* Хайлт/ойролцоох хаяг татаж байх үед хоосон төлөв андуурагдахгүй */}
                <Spin spinning={searching}>
                  {addressMap?.length ? (
                    <Radio.Group
                      value={selectedAddress?.addressId}
                      onChange={(e) => onSelectAddress(e.target.value)}
                      className="pp_map_panel__radios"
                    >
                      {addressMap.map((address) => (
                        <Radio key={address.addressId} value={address.addressId}>
                          <Space direction="vertical" size={0}>
                            <Text>{address.name}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {address.nameFull ?? address.nameShort}
                            </Text>
                          </Space>
                          {selectedAddress?.addressId === address.addressId && (
                            <CiLocationOn className="pp_map_panel__check" size={20} />
                          )}
                        </Radio>
                      ))}
                    </Radio.Group>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Газрын зураг дээр дарж эсвэл хайлт хийж байршлаа сонгоно уу.
                        </Text>
                      }
                      style={{ margin: 0, paddingTop: 48 }}
                    />
                  )}
                </Spin>
              </div>
            </Space>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default BranchLocationPicker;
